import { useState, useEffect } from 'react'
import { sharepointImageService, SharePointImageFile } from '../utils/sharepointImageService'

interface SharePointImagePickerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (image: SharePointImageFile) => void
  title?: string
  allowUpload?: boolean
  category?: 'classes' | 'kits' | 'gallery'
}

function SharePointImagePicker({
  isOpen,
  onClose,
  onSelect,
  title = 'Select Kit Image',
  allowUpload = true,
  category = 'kits'
}: SharePointImagePickerProps) {
  const [sharePointImages, setSharePointImages] = useState<SharePointImageFile[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadImages()
    }
  }, [isOpen])

  const loadImages = async () => {
    setLoading(true)
    try {
      // Get ALL SharePoint images with local cache info
      const allImages = await sharepointImageService.getImagesWithLocalInfo()

      // Show images of the specified category first, then others
      const categoryImages = allImages.filter(img => img.category === category)
      const otherImages = allImages.filter(img => img.category !== category)

      // Combine them with category images first
      const sortedImages = [...categoryImages, ...otherImages]
      setSharePointImages(sortedImages)
    } catch (error) {
      console.error('Failed to load images:', error)
    }
    setLoading(false)
  }

  const handleSharePointImageSelect = async (image: SharePointImageFile) => {
    // If image is not cached locally, download and cache it first
    if (!image.localDataUrl) {
      setLoading(true)
      try {
        const downloadResult = await sharepointImageService.downloadImageForLocalUse(image)
        if (downloadResult.success && downloadResult.localDataUrl) {
          // Update the image object with the local data URL
          image.localDataUrl = downloadResult.localDataUrl
          image.isSelected = true
        } else {
          alert(`Failed to cache image: ${downloadResult.message}`)
          setLoading(false)
          return
        }
      } catch (error) {
        alert('Failed to cache image for use')
        setLoading(false)
        return
      }
      setLoading(false)
    }

    onSelect(image)
    onClose()
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      console.log(`📤 Starting upload of ${files.length} file(s) to SharePoint (category: ${category})`)

      for (const file of Array.from(files)) {
        console.log(`📁 Uploading file: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`)

        const result = await sharepointImageService.uploadFromLocalFile(file, category)

        console.log('Upload result:', result)

        if (result.success) {
          console.log(`✅ Successfully uploaded: ${file.name}`)
          await loadImages()
        } else {
          console.error(`❌ Upload failed for ${file.name}:`, result.message)
          alert(`Failed to upload ${file.name}: ${result.message}`)
        }
      }

      console.log('✅ All uploads complete')
    } catch (error) {
      console.error('❌ Upload exception:', error)
      alert(`Upload error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
    setUploading(false)

    // Clear the input
    event.target.value = ''
  }

  const filteredSharePointImages = sharePointImages.filter(image =>
    image.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (!isOpen) return null

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4"
         style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999999 }}>
      <div className="bg-white rounded w-100" style={{ maxWidth: '72rem', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="p-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fs-4 fw-bold mb-0">{title}</h3>
            <button
              onClick={onClose}
              className="btn-close btn-close-lg fs-2 text-muted border-0 bg-transparent"
              style={{ fontSize: '2rem' }}
            >
              ×
            </button>
          </div>

          {/* Info Message */}
          <div className="alert alert-info mb-4">
            <p className="small mb-0">
              <strong>All SharePoint images are shown below.</strong> {category.charAt(0).toUpperCase() + category.slice(1)} images are prioritized at the top.
              {sharePointImages.some(img => img.category !== category) &&
                ` Use the 'Move to ${category}' button to recategorize images.`}
            </p>
          </div>

          {/* Search and Upload */}
          <div className="row g-3 mb-4">
            <div className="col-md-8">
              <input
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
            {allowUpload && (
              <div className="col-md-4">
                <label className={`btn w-100 text-white ${
                  uploading
                    ? 'btn-secondary'
                    : 'btn-success'
                }`} style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? 'Uploading...' : 'Upload to SharePoint'}
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.heic,.heif,image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/bmp"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="visually-hidden"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="py-5 text-center">
              <div className="spinner-border text-primary mx-auto d-block" role="status" style={{ width: '2rem', height: '2rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading images...</p>
            </div>
          ) : (
            <div>
              {filteredSharePointImages.length === 0 ? (
                <div className="py-5 text-center">
                  <div className="fs-1 mb-3">📷</div>
                  <p className="text-muted fs-5">No SharePoint images available</p>
                  <p className="text-muted small">
                    {sharePointImages.length === 0
                      ? 'Upload images using the button above or from the SP Images admin page'
                      : 'Try adjusting your search criteria'}
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {filteredSharePointImages.map((image) => (
                    <div className="col-6 col-md-4 col-lg-3 col-xl-2" key={image.id}>
                      <div
                        onClick={() => handleSharePointImageSelect(image)}
                        className="position-relative border border-2 border-light-subtle rounded overflow-hidden"
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--bs-primary)'
                          e.currentTarget.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = ''
                          e.currentTarget.style.boxShadow = ''
                        }}
                      >
                        <div className="bg-light d-flex align-items-center justify-content-center"
                             style={{ aspectRatio: '1/1' }}>
                          {image.localDataUrl ? (
                            <img
                              src={image.localDataUrl}
                              alt={image.name}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : image.thumbnailUrl ? (
                            <img
                              src={image.thumbnailUrl}
                              alt={image.name}
                              className="w-100 h-100 object-fit-cover opacity-75"
                            />
                          ) : (
                            <div style={{ fontSize: '2rem' }}>🖼️</div>
                          )}
                        </div>
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-0 opacity-0"
                             style={{ transition: 'all 0.3s ease' }}
                             onMouseEnter={(e) => {
                               e.currentTarget.classList.add('bg-opacity-50', 'opacity-100')
                             }}
                             onMouseLeave={(e) => {
                               e.currentTarget.classList.remove('bg-opacity-50', 'opacity-100')
                             }}>
                          <div className="d-flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSharePointImageSelect(image)
                              }}
                              className="btn btn-primary btn-sm"
                            >
                              {image.localDataUrl ? 'Select' : 'Download & Select'}
                            </button>
                            {image.category !== category && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  const result = await sharepointImageService.updateImageCategory(image.id, category)
                                  if (result.success) {
                                    await loadImages()
                                  } else {
                                    alert(`Failed to update category: ${result.message}`)
                                  }
                                }}
                                className="btn btn-warning btn-sm"
                                title={`Move to ${category} category`}
                                style={{ fontSize: '0.75rem' }}
                              >
                                Move to {category}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Cache status indicator */}
                        <div className="position-absolute top-0 end-0 m-2">
                          {image.localDataUrl ? (
                            <span className="badge bg-success text-white" style={{ fontSize: '0.75rem' }}>
                              Cached
                            </span>
                          ) : (
                            <span className="badge bg-warning text-white" style={{ fontSize: '0.75rem' }}>
                              Not Cached
                            </span>
                          )}
                        </div>

                        <div className="p-2 bg-white">
                          <div className="small fw-medium text-truncate" title={image.name}>
                            {image.name}
                          </div>
                          <div className="small text-muted">
                            <span className={`badge me-1 ${
                              image.category === category
                                ? 'bg-success-subtle text-success'
                                : 'bg-warning-subtle text-warning'
                            }`} style={{ fontSize: '0.65rem' }}>
                              {image.category}
                            </span>
                            | {(image.size / 1024).toFixed(1)}KB
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SharePointImagePicker
